import type { Provider } from "@src/interfaces/provider.interface";

/**
 * Maps API data to a list of Provider objects.
 * @param apiData - The raw data from the API.
 * @returns A list of Provider objects.
 */
export function mapProviderList(apiData: unknown[]): Provider[] {
  if (!Array.isArray(apiData) || apiData.length === 0) {
    return [];
  }

  return apiData
    .map((item) => {
      // Ensure item is an object
      if (typeof item !== "object" || item === null) {
        console.warn("Invalid item in API data:", item);
        return null;
      }

      return {
        ...item,
        firstName: toTitleCaseIfString(item["providerFirstName"]),
        lastName: toTitleCaseIfString(item["providerLastName"]),
        fullName: toTitleCaseIfString(item["providerFullName"]),
        preferredName: toTitleCaseIfString(item["providerPreferredName"]),
        preferredPronouns: toTitleCaseIfString(item["providerPreferredPronouns"]),
        gender: toTitleCaseIfString(item["providerGender"]),
        languages: toTitleCaseIfString(item["providerLanguages"]),
        primaryLocation:
          typeof item["providerPrimaryLocation"] === "string"
            ? item["providerPrimaryLocation"]
                .replace(/^[^,]+/, (city) => toTitleCase(city))
                .replace(
                  /,\s*([A-Za-z]{2})$/,
                  (_match, state) => `, ${String(state).toUpperCase()}`,
                )
            : "",
        newHire: parseBoolean(item["providerNewHire"]),
        acceptsEAP: parseBoolean(item["acceptsEAP"]),
        modality: Array.isArray(item["modality"])
          ? item["modality"].map((modality) => String(modality)).sort((a, b) => b.localeCompare(a)) // Sort in descending order
          : [],
        departments: Array.isArray(item["departments"])
          ? item["departments"].map((dept) => ({
              ...dept,
              city: toTitleCaseIfString(dept["city"]),
            }))
          : [],
      };
    })
    .filter((provider): provider is Provider => provider !== null); // Filter out invalid items
}

/**
 * Converts a string to title case.
 * @param str - The string to convert.
 * @returns The title-cased string. Eg: JOHN DOE -> John Doe
 */
function toTitleCase(str: string): string {
  return str
    .toLowerCase() // Convert the entire string to lowercase
    .replace(/\b\w/g, (char) => char.toUpperCase()); // Capitalize the first letter of each word
}

/**
 * Converts a value to title case if it is a string.
 * @param value - The value to convert.
 * @returns The title-cased string, or an empty string if the value is not a string.
 */
function toTitleCaseIfString(value: unknown): string {
  return typeof value === "string" ? toTitleCase(value) : "";
}

/**
 * Parses a string into a boolean value.
 * @param value - The string to parse.
 * @returns The boolean value.
 */
function parseBoolean(value: unknown): boolean {
  if (typeof value === "string") {
    if (value.toLowerCase() === "true" || value.toLowerCase() === "yes") {
      return true;
    }
    return false;
  }
  return false; // Default return value for non-string inputs
}
